import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 历史大事件 - 横屏「电影时间线」风格
 *
 * 核心亮点：
 * - 电影宽银幕比例 + 上下黑边
 * - 时间轴从左到右绘制（SVG 路径动画）
 * - 年代节点弹入 + 事件卡片展开
 * - 纪元色带（不同时代不同色调）
 * - 老照片颗粒噪点效果
 * - 时间轴扫描光线
 *
 * 尺寸：1920x1080 横屏
 */

interface HistoryEvent {
  year: string;
  title: string;
  detail: string;
  era: string;
  color: string;
  icon: string;
}

interface HistoryTimelineTemplateProps {
  topic: string;
  subtitle: string;
  events: HistoryEvent[];
  conclusion: string;
  timespan: string;
}

const DEFAULT_PROPS: HistoryTimelineTemplateProps = {
  topic: '人类通信技术演进史',
  subtitle: 'A Brief History of Human Communication',
  events: [
    { year: '公元前 3000', title: '楔形文字', detail: '美索不达米亚发明书写系统', era: '古代', color: '#d4a847', icon: '📜' },
    { year: '105 年', title: '造纸术', detail: '蔡伦改进造纸工艺，信息存储革命', era: '古代', color: '#d4a847', icon: '📄' },
    { year: '1440', title: '活字印刷', detail: '古腾堡发明铅活字，知识大众化', era: '中世纪', color: '#b87333', icon: '📖' },
    { year: '1837', title: '电报', detail: '莫尔斯发明电报，信息首次以电速传播', era: '工业革命', color: '#6b8e23', icon: '⚡' },
    { year: '1876', title: '电话', detail: '贝尔发明电话，实时语音通信', era: '工业革命', color: '#6b8e23', icon: '📞' },
    { year: '1969', title: 'ARPANET', detail: '互联网雏形，4 台计算机首次联网', era: '信息时代', color: '#4a90d9', icon: '🌐' },
    { year: '2007', title: 'iPhone', detail: '智能手机重新定义移动通信', era: '信息时代', color: '#4a90d9', icon: '📱' },
    { year: '2023', title: 'GPT-4', detail: '大语言模型开启人机自然对话新纪元', era: '智能时代', color: '#9b59b6', icon: '🤖' },
  ],
  conclusion: '从泥板到 AI，人类用 5000 年将通信延迟从数月缩短到毫秒',
  timespan: '5000 年通信史',
};

export const HistoryTimelineTemplate: React.FC<Partial<HistoryTimelineTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Layout
  const barH = 50; // cinematic letterbox
  const timelineY = 520;
  const timelineX1 = 120;
  const timelineX2 = 1800;
  const nodeSpacing = (timelineX2 - timelineX1) / (p.events.length - 1);

  // Phases
  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });
  const timelineDraw = interpolate(frame, [40, 40 + p.events.length * 30], [0, 1], { extrapolateRight: 'clamp' });

  // Scan line
  const scanX = timelineX1 + (timelineX2 - timelineX1) * timelineDraw;

  // Events
  const eventStart = 50;
  const eventInterval = 45;

  // Conclusion
  const conclusionStart = eventStart + p.events.length * eventInterval + 30;
  const conclusionOpacity = interpolate(frame, [conclusionStart, conclusionStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Film grain effect
  const grainSeed = frame * 7.31;

  return (
    <AbsoluteFill
      style={{
        background: '#0c0c0c',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Film grain overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' seed='${Math.floor(grainSeed)}' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`,
          backgroundSize: '128px 128px',
          opacity: 0.5,
          pointerEvents: 'none',
        }}
      />

      {/* Letterbox bars */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: barH, background: 'black', zIndex: 10 }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: barH, background: 'black', zIndex: 10 }} />

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: barH + 20,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: headerProgress,
          transform: `translateY(${(1 - headerProgress) * -10}px)`,
        }}
      >
        <div style={{ display: 'inline-flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
          <div style={{ padding: '4px 14px', background: 'rgba(212,168,71,0.1)', border: '1px solid rgba(212,168,71,0.25)', borderRadius: 8, fontSize: 20, color: '#d4a847', fontWeight: 600 }}>
            📜 HISTORY
          </div>
          <span style={{ fontSize: 20, color: 'rgba(255,255,255,0.3)' }}>{p.timespan}</span>
        </div>
        <h1 style={{ fontSize: 42, fontWeight: 800, margin: 0, letterSpacing: '-0.01em' }}>{p.topic}</h1>
        <p style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginTop: 6, fontStyle: 'italic' }}>{p.subtitle}</p>
      </div>

      {/* ========== TIMELINE SVG ========== */}
      <svg style={{ position: 'absolute', inset: 0 }} width="1920" height="1080" viewBox="0 0 1920 1080">
        {/* Main timeline line */}
        <line
          x1={timelineX1}
          y1={timelineY}
          x2={timelineX1 + (timelineX2 - timelineX1) * timelineDraw}
          y2={timelineY}
          stroke="rgba(255,255,255,0.15)"
          strokeWidth="2"
        />

        {/* Scan line glow */}
        <line
          x1={scanX - 40}
          y1={timelineY}
          x2={scanX}
          y2={timelineY}
          stroke="#d4a847"
          strokeWidth="3"
          opacity="0.6"
        />
        <circle cx={scanX} cy={timelineY} r="5" fill="#d4a847" opacity="0.8" />
        <circle cx={scanX} cy={timelineY} r="12" fill="#d4a847" opacity="0.1" />

        {/* Era color bands */}
        {(() => {
          let prevEra = '';
          let bandStart = timelineX1;
          const bands: { x1: number; x2: number; color: string; era: string }[] = [];

          p.events.forEach((evt, i) => {
            const x = timelineX1 + i * nodeSpacing;
            if (evt.era !== prevEra && prevEra) {
              bands.push({ x1: bandStart, x2: x, color: p.events[i - 1].color, era: prevEra });
              bandStart = x;
            }
            prevEra = evt.era;
            if (i === p.events.length - 1) {
              bands.push({ x1: bandStart, x2: x, color: evt.color, era: evt.era });
            }
          });

          return bands.map((band, i) => {
            const rangeStart = band.x1 / timelineX2;
            const rangeEnd = Math.max(band.x2 / timelineX2, rangeStart + 0.001);
            const drawProgress = interpolate(timelineDraw, [rangeStart, rangeEnd], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            });
            return (
              <g key={i}>
                <rect
                  x={band.x1}
                  y={timelineY + 8}
                  width={(band.x2 - band.x1) * drawProgress}
                  height={6}
                  rx={3}
                  fill={band.color}
                  opacity="0.3"
                />
                <text
                  x={(band.x1 + band.x2) / 2}
                  y={timelineY + 40}
                  fill={band.color}
                  fontSize="22"
                  textAnchor="middle"
                  opacity={drawProgress * 0.6}
                >
                  {band.era}
                </text>
              </g>
            );
          });
        })()}

        {/* Event nodes */}
        {p.events.map((evt, i) => {
          const start = eventStart + i * eventInterval;
          const nodeProgress = spring({
            frame: frame - start,
            fps,
            from: 0,
            to: 1,
            config: { damping: 12, stiffness: 150, mass: 0.5 },
          });
          if (nodeProgress < 0.01) return null;

          const x = timelineX1 + i * nodeSpacing;
          const isAbove = i % 2 === 0;
          const cardY = isAbove ? timelineY - 200 : timelineY + 50;

          return (
            <g key={i} opacity={nodeProgress}>
              {/* Connector line */}
              <line
                x1={x}
                y1={timelineY}
                x2={x}
                y2={isAbove ? timelineY - 30 : timelineY + 30}
                stroke={evt.color}
                strokeWidth="1.5"
                opacity="0.4"
                strokeDasharray="4 3"
              />

              {/* Node dot */}
              <circle cx={x} cy={timelineY} r={8 * nodeProgress} fill={evt.color} opacity="0.9" />
              <circle cx={x} cy={timelineY} r={14 * nodeProgress} fill={evt.color} opacity="0.12" />

              {/* Year label */}
              <text
                x={x}
                y={isAbove ? timelineY + 48 : timelineY - 16}
                fill="rgba(255,255,255,0.5)"
                fontSize="22"
                fontWeight="bold"
                textAnchor="middle"
              >
                {evt.year}
              </text>
            </g>
          );
        })}
      </svg>

      {/* ========== EVENT CARDS (HTML overlays) ========== */}
      {p.events.map((evt, i) => {
        const start = eventStart + i * eventInterval;
        const cardScale = spring({
          frame: frame - start,
          fps,
          from: 0.8,
          to: 1,
          config: { damping: 14, stiffness: 120, mass: 0.5 },
        });
        const cardOpacity = interpolate(frame, [start, start + 15], [0, 1], { extrapolateRight: 'clamp' });
        if (cardOpacity < 0.05) return null;

        const x = timelineX1 + i * nodeSpacing;
        const isAbove = i % 2 === 0;
        const cardTop = isAbove ? timelineY - 260 : timelineY + 75;

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: x - 90,
              top: cardTop,
              width: 180,
              padding: '14px',
              background: `${evt.color}0c`,
              borderRadius: 14,
              border: `1px solid ${evt.color}30`,
              opacity: cardOpacity,
              transform: `scale(${cardScale})`,
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 30, marginBottom: 4 }}>{evt.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: 'white', marginBottom: 4 }}>{evt.title}</div>
            <div style={{ fontSize: 20, color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>{evt.detail}</div>
          </div>
        );
      })}

      {/* Conclusion */}
      <div
        style={{
          position: 'absolute',
          bottom: barH + 30,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: conclusionOpacity,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '14px 36px',
            background: 'rgba(212,168,71,0.08)',
            border: '1px solid rgba(212,168,71,0.2)',
            borderRadius: 14,
            fontSize: 20,
            color: 'rgba(255,255,255,0.85)',
          }}
        >
          💡 {p.conclusion}
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: barH,
          left: 0,
          width: `${interpolate(frame, [0, conclusionStart + 40], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 2,
          background: 'linear-gradient(90deg, #d4a847, #9b59b6)',
          zIndex: 11,
        }}
      />
    </AbsoluteFill>
  );
};
